/**
 * The shell: which screen is showing, and the one piece of state that spans them —
 * the session currently being prepared or played.
 *
 * Deliberately a small state machine rather than a router. There are seven screens and
 * the transitions between them are the product; a URL scheme would add ceremony without
 * adding anything a user could use.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Recording, SessionPlan } from './types';
import { UnreachableError, planFingerprint } from './session/plan';
import { assertEligible, IneligibleSessionError } from './session/eligibility';
import { prepareSessionStart, StaleSessionStartError } from './session/start';
import { resolveAssembly } from './composition/lineage';
import { saveResolvedSession, finalizeArchivedSession } from './state/session-archive';
import { intent as timingIntent, nowMs, startClock, targetSec, type TimingIntent } from './state/timing';
import { NoCandidatesError, selectionPool, type SelectionContext } from './session/select';
import { type Choice, drawAnother, createHomeChoiceMemory, pickChoice } from './session/choice';
import { loadManifest, loading, manifest, manifestError } from './state/manifest';
import { history, finishPlay, recordPlay } from './state/history';
import { settings, updateSettings } from './state/settings';
import { ByteStore, SourceLoader, detectContainers } from './audio/sources';
import type { Container } from './audio/sources';
import { WebAudioEngine } from './audio/engine';
import type { AudioEngine, EngineEvent } from './audio/engine';
import { NativeAudioEngine } from './audio/native-engine';
import { Brahm, BRIDGE_VERSION, bridgeIsCompatible } from './audio/native-bridge';
import { FRAME_RATE, PROTOCOL_VERSION } from './audio/native-protocol';
import { isNativeShell } from './config/origin';
import { Home, type HomePreview } from './ui/Home';
import { canBegin } from './ui/TimeSpan';
import { Player } from './ui/Player';
import { Library } from './ui/Library';
import { Settings } from './ui/Settings';
import { Account } from './ui/Account';
import { Debug } from './ui/Debug';
import { Onboarding } from './ui/Onboarding';
import { report } from './telemetry';
import { sitInProgress } from './state/update';
import { contextStateIsAnomaly, trace } from './audio/trace';
import { guardBack, guardNativeBack } from './state/back-guard';
import {
  diagnosticsNotice, sweepStoredSessions, uploadClosedSession, sweepPreviewChunks,
  type DiagnosticsNotice,
} from './diagnostics';
import {
  PlaybackIntentMachine,
  RecoveryController,
  requestUserPlayback,
  type PlaybackIntent,
  type RecoveryAttempt,
  type RecoveryTrigger,
  type RendererState,
} from './audio/lifecycle';
import { WakeLockController, type WakeLockResult } from './state/wake-lock';
import { MediaSessionController } from './audio/media-session';
import {
  finishSitMarker,
  markSitInterrupted,
  noteSitLifecycle,
  startSitMarker,
  takeInterruptedSit,
} from './state/sit-lifecycle';
import { Done } from './ui/Done';
import { BuildMark } from './ui/BuildMark';
import { previewPayload, previewTrace } from './session/preview-trace';
import type { ChoiceReason, PreviewResolved, Trigger } from './session/preview-schema';
import { BUILD_COMMIT, BUILD_DIRTY } from './ui/build-info';

type Screen = 'home' | 'player' | 'library' | 'settings' | 'account' | 'debug' | 'done';

interface Prepared {
  recording: Recording;
  plan: SessionPlan;
  timing: TimingIntent;
  preview: PreviewResolved | null;
  release: { id: string; assetBase: string; digest?: string; manifestDigest?: string };
}

const PAGE_INSTANCE_ID = typeof crypto !== 'undefined' && 'randomUUID' in crypto
  ? crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

const WAS_DISCARDED = typeof document !== 'undefined'
  && (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true;

interface Interruption {
  atSec: number;
  message: string;
  clearsOnProgress: boolean;
  active: boolean;
}

export function App() {
  const [timingReady, setTimingReady] = useState(false);
  const [screen, setScreen] = useState<Screen>('home');
  useEffect(() => {
    if (isNativeShell()) void Brahm.updateContext({ home: screen === 'home' }).catch(() => {});
  }, [screen]);
  const choiceRef = useRef<Choice | null>(null);
  const cellMemory = useRef(createHomeChoiceMemory());
  const [homeResolution, setHomeResolution] = useState<{
    choice: Choice | null; preview: HomePreview | null; payload: ReturnType<typeof previewPayload>;
  } | null>(null);
  const choice = homeResolution?.choice ?? null;
  const previewRef = useRef<PreviewResolved | null>(null);
  const pendingTrigger = useRef<Trigger | null>(null);
  const [homeAction, setHomeAction] = useState(0);
  const previousInput = useRef<{target: number; cadence: typeof settings.value.cadence;
    intent: TimingIntent; manifest: typeof manifest.value; history: typeof history.value} | null>(null);
  const historyRevision = useRef(0);
  const [drawProblem, setDrawProblem] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [prepared, setPrepared] = useState<Prepared | null>(null);
  const [confirmingExit, setConfirmingExit] = useState(false);
  const [position, setPosition] = useState(0);
  const [playbackIntent, setPlaybackIntent] = useState<PlaybackIntent>('ended');
  const [rendererPlaying, setRendererPlaying] = useState(false);
  const [wallElapsed, setWallElapsed] = useState(0);
  const [interruption, setInterruption] = useState<Interruption | null>(null);
  const [stalled, setStalled] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [diagnosticsStatus, setDiagnosticsStatus] = useState<DiagnosticsNotice | null>(null);

  // The sit's own copy, for the engine's event handler: it is registered inside begin()
  // and would otherwise close over whatever `prepared` was before that sit was set.
  const preparedRef = useRef<Prepared | null>(null);
  const engineRef = useRef<AudioEngine | null>(null);
  const startGenerationRef = useRef(0);
  const startingRef = useRef(false);
  const closingRef = useRef(0);
  const screenRef = useRef(screen);
  screenRef.current = screen;
  const canUploadPreviews = () => screenRef.current === 'home'
    && !startingRef.current && !closingRef.current && !engineRef.current;
  const archiveWriteRef = useRef<Promise<unknown>>(Promise.resolve());
  const contextRef = useRef<AudioContext | null>(null);
  const contextCleanupRef = useRef<(() => void) | null>(null);
  const recoveryRef = useRef<RecoveryController | null>(null);
  const wakeRef = useRef<WakeLockController | null>(null);
  const mediaRef = useRef<MediaSessionController | null>(null);
  const wallStartedRef = useRef<number | null>(null);
  const wallTimerRef = useRef<number | null>(null);
  const diagnosticsTimerRef = useRef<number | null>(null);
  const interruptionRef = useRef<Interruption | null>(null);
  const rendererPlayingRef = useRef(false);
  const reportedStallsRef = useRef(new Set<string>());
  const intentMachineRef = useRef<PlaybackIntentMachine | null>(null);
  if (!intentMachineRef.current) {
    intentMachineRef.current = new PlaybackIntentMachine(setPlaybackIntent);
  }
  const intentMachine = intentMachineRef.current;
  const s = settings.value;

  useEffect(() => { void loadManifest(); }, []);

  // Seed the span from the length they used last time, then keep a clock running so
  // a fixed finish time counts down and can lapse on screen rather than silently.
  useEffect(() => {
    timingIntent.value = { kind: 'duration', durationSec: settings.value.lastDurationSec };
    setTimingReady(true);
    return startClock();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- first mount only
  }, []);

  useEffect(() => {
    trace.noteNextPage({ pageInstanceId: PAGE_INSTANCE_ID, wasDiscarded: WAS_DISCARDED });
    // This runs after the interrupted predecessor is annotated. A killed page never
    // reaches close(), so the next live page is the only reliable delivery opportunity.
    sweepStoredSessions(trace.recent());
    sweepPreviewChunks(canUploadPreviews);
    const previous = takeInterruptedSit();
    if (previous) {
      report({
        event: 'session_incomplete',
        targetBucketMin: previous.targetBucketMin,
        cadence: previous.cadence,
        outcome: previous.interrupted ? 'interrupted' : 'abandoned',
        reason: WAS_DISCARDED ? 'discarded' : (previous.reason ?? previous.lastLifecycle ?? 'unknown'),
      });
    }
  }, []);

  // Theme: the tag on <html> is what the CSS keys off, and "system" means no tag.
  useEffect(() => {
    const el = document.documentElement;
    if (s.theme === 'system') el.removeAttribute('data-theme');
    else el.setAttribute('data-theme', s.theme);
  }, [s.theme]);

  // ------------------------------------------------------------ preparing

  const selection = (target: number): SelectionContext => ({
    history: history.value,
    now: Date.now(),
    targetSec: target,
    cadence: s.cadence,
    toleranceSec: timingIntent.value.kind === 'deadline' ? 0 : 30,
    desiredSpeechRatio: s.desiredSpeechRatio ?? undefined,
  });

  const planProblem = (e: unknown): string => {
    // Every evaluator reason gets a sentence written for a person. The evaluator's own
    // detail ("No safe composition is in the requested speech band.") is for the trace:
    // it reached the screen once approved cuts changed a hand-picked recording's reason.
    if (e instanceof IneligibleSessionError) {
      const reach = e.evaluation.reach;
      if (e.evaluation.reason === 'duration' && reach) {
        return `That recording runs ${Math.round(reach.minSec / 60)}–${Math.round(reach.maxSec / 60)} minutes.`;
      }
      return 'This recording cannot fit these settings while keeping its guidance intact. Choose Something else or change the length.';
    }
    if (e instanceof NoCandidatesError) {
      return e.reason === 'duration'
        ? 'No recording can be made that length. Try asking for a little longer.'
        : 'Nothing matches those themes at this length.';
    }
    if (e instanceof UnreachableError) {
      return `That recording runs ${Math.round(e.reach.minSec / 60)}–`
        + `${Math.round(e.reach.maxSec / 60)} minutes.`;
    }
    return (e as Error).message;
  };

  // The home screen's preview follows the length and the cadence as they are chosen. The
  // span is read on every clock tick, because a fixed finish time shortens the sit by a
  // second a second; the recording itself only changes when it can no longer make it.
  const it = timingIntent.value;
  const now = nowMs.value;
  const homeTarget = targetSec(it, now);
  const homeReady = screen === 'home' && manifest.value !== null
    && canBegin(it, s.cadence, now);

  useLayoutEffect(() => {
    const m = manifest.value;
    if (screen !== 'home' || !m || !s.onboarded || !timingReady) return;
    const previous = previousInput.current;
    const trigger: Trigger = pendingTrigger.current ?? (!previous ? 'initial'
      : previous.manifest !== m ? 'manifest-refresh'
      : previous.history !== history.value ? 'history-refresh'
      : previous.cadence !== s.cadence ? 'cadence'
      : previous.intent !== it ? 'length' : 'deadline-tick');
    pendingTrigger.current = null;
    if (previous && previous.history !== history.value) historyRevision.current++;
    previousInput.current = {target:homeTarget,cadence:s.cadence,intent:it,manifest:m,history:history.value};
    const before = choiceRef.current;
    let next = before, reason: ChoiceReason = trigger === 'library-pick' ? 'restored' : 'kept';
    let preview: HomePreview | null = null, failure: string | null = null, failureCode: string | null = null;
    const t0 = performance.now();
    const ctx = selection(homeTarget);
    try {
      if (homeReady) {
        if (trigger === 'another' || trigger === 'library-pick') cellMemory.current.clear();
        if (trigger === 'another') next = drawAnother(before,m,ctx,r => {reason=r;});
        const resolved = cellMemory.current.resolve(next,m,ctx,it.kind === 'deadline',r => {if(trigger !== 'another' && trigger !== 'library-pick') reason=r;});
        next = resolved.choice;
        preview = {recording:next.recording,plan:resolved.plan,problem:null};
      } else failureCode = 'timing-not-ready';
    } catch (error) {
      failure = planProblem(error);
      failureCode = error instanceof IneligibleSessionError ? error.evaluation.reason
        : error instanceof NoCandidatesError ? error.reason : 'planning-error';
      // An empty length-and-cadence cell never gets here: canBegin already asks the same
      // banded evaluator, disables Sit, and the length note under the field says why.
      if (next && homeReady) preview = {recording:next.recording,plan:null,problem:failure};
    }
    let pool = {poolSize:0,freshPoolSize:0,recencyRelaxed:false};
    try { pool = selectionPool(m,ctx); } catch { /* Empty pool is evidence too. */ }
    choiceRef.current = next;
    setDrawProblem(preview ? null : failure);
    setHomeResolution({choice:next,preview,payload:previewPayload({manifest:m,recording:next?.recording ?? null,
      plan:preview?.plan ?? null,target:homeTarget,cadence:s.cadence,intentKind:it.kind,trigger,reason,
      previousRecordingId:before?.recording.id ?? null,picked:next?.picked ?? false,
      historyRevision:historyRevision.current,pool,plannerVersion:`${BUILD_COMMIT}${BUILD_DIRTY ? '+local' : ''}`,
      planMs:performance.now()-t0,problem:failureCode,desiredSpeechRatio:ctx.desiredSpeechRatio})});
    // Choice, plan and diagnostic facts are one state update; the next commit renders them together.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, timingReady, homeReady, homeTarget, s.cadence, s.desiredSpeechRatio, s.onboarded, it,
    manifest.value, history.value, homeAction]);

  useLayoutEffect(() => {
    if (screen === 'home' && homeResolution && s.onboarded) {
      previewRef.current = previewTrace.record(homeResolution.payload);
    }
  }, [homeResolution]);

  useEffect(() => {
    if (screen !== 'home') {
      previewTrace.leave(); previousInput.current = null; cellMemory.current.clear();
    }
  }, [screen]);

  const homePreview = homeResolution?.preview ?? null;
  const somethingElse = () => {
    pendingTrigger.current = 'another'; setHomeAction(n => n+1);
  };

  const sit = () => {
    cellMemory.current.clear();
    if (!choice || !homePreview?.plan || starting || startingRef.current) return;
    setProblem(null);
    // Resolve the span once, here, and plan again rather than trusting the preview. If the
    // user fixed a finish time, the length depends on when Sit was actually pressed — the
    // band above the button was drawn up to a second ago, and planning from it would
    // finish late by however long they looked at it.
    const target = targetSec(timingIntent.value);
    const release = manifest.value;
    const recording = release?.recordings.find(r => r.id === choice.recording.id);
    if (!release || !recording) {
      setProblem('That recording is no longer in the library. Choose another recording.');
      return;
    }
    let plan: SessionPlan;
    try {
      plan = assertEligible({ recording, targetSec: target, cadence: s.cadence,
        toleranceSec: timingIntent.value.kind === 'deadline' ? 0 : 30,
        desiredSpeechRatio: s.desiredSpeechRatio ?? undefined }).plan;
    } catch (e) {
      setProblem(planProblem(e));
      return;
    }
    // Remember the length, never the wall-clock deadline: 07:30 is meaningless
    // by tomorrow, but "about 25 minutes" is a real preference.
    updateSettings({ lastDurationSec: Math.round(target / 60) * 60 });
    const next: Prepared = { recording, plan, timing: { ...timingIntent.value }, preview: previewRef.current,
      release: { id: release.generatedAt, assetBase: release.assetBase, manifestDigest: release.manifestDigest,
        ...(release.releaseDigest ? { digest: release.releaseDigest } : {}) } };
    preparedRef.current = next;
    setPrepared(next);
    void begin(next);
  };

  // ------------------------------------------------------------ playing

  const setAudioInterruption = (
    message: string,
    atSec = engineRef.current?.currentTime ?? position,
    clearsOnProgress = true,
  ) => {
    const value = { message, atSec, clearsOnProgress, active: true };
    interruptionRef.current = value;
    setInterruption(value);
  };

  const clearAudioInterruption = () => {
    interruptionRef.current = null;
    setInterruption(null);
  };

  const markAudioRecovered = () => {
    const interrupted = interruptionRef.current;
    if (!interrupted) return;
    const value = {
      ...interrupted,
      active: false,
      message: 'Meditation audio was interrupted. It resumed from the same programme position; '
        + 'no speech was skipped.',
    };
    interruptionRef.current = value;
    setInterruption(value);
  };

  const setRendererCondition = (value: boolean) => {
    rendererPlayingRef.current = value;
    setRendererPlaying(value);
  };

  const updateWallElapsed = () => {
    const started = wallStartedRef.current;
    if (started !== null) setWallElapsed(Math.max(0, (Date.now() - started) / 1000));
  };

  const startWallElapsed = () => {
    wallStartedRef.current = Date.now();
    setWallElapsed(0);
    if (wallTimerRef.current !== null) clearInterval(wallTimerRef.current);
    wallTimerRef.current = setInterval(updateWallElapsed, 1000) as unknown as number;
  };

  const stopWallElapsed = () => {
    if (wallTimerRef.current !== null) clearInterval(wallTimerRef.current);
    wallTimerRef.current = null;
    wallStartedRef.current = null;
  };

  const reportStall = (reason: string) => {
    if (reportedStallsRef.current.has(reason)) return;
    reportedStallsRef.current.add(reason);
    markSitInterrupted(reason);
    report({ event: 'stall', reason });
  };

  const syncMediaSession = () => {
    const engine = engineRef.current;
    const ctx = contextRef.current;
    if (!engine) return;
    mediaRef.current?.sync(
      intentMachine.intent,
      rendererPlayingRef.current && engine.playing && (ctx?.state as RendererState) === 'running'
        && interruptionRef.current?.active !== true,
      engine.currentTime,
      engine.duration,
    );
  };

  const onRecoveryAttempt = (attempt: RecoveryAttempt) => {
    trace.log('recovery', {
      trigger: attempt.trigger,
      result: attempt.result,
      error: attempt.error ?? null,
      contextState: contextRef.current?.state ?? null,
      visibility: document.visibilityState,
      intent: intentMachine.intent,
    });
    if (attempt.result === 'timed-out' || attempt.result === 'rejected') {
      setAudioInterruption('Meditation audio could not restart. Your actual sitting time is still shown.');
      reportStall(`recovery-${attempt.result}`);
    } else if (attempt.result === 'recovered' && interruptionRef.current === null
        && engineRef.current?.playing
        && (contextRef.current?.state as RendererState) === 'running') {
      setRendererCondition(true);
    }
    syncMediaSession();
  };

  const attemptRecovery = (trigger: RecoveryTrigger) => {
    engineRef.current?.inspectClock(intentMachine.intent);
    const recovery = recoveryRef.current;
    if (recovery) void recovery.trigger(trigger);
  };

  const resumeUserPlayback = () => {
    intentMachine.dispatch('user-play');
    const engine = engineRef.current;
    if (!engine) return;
    const generation = startGenerationRef.current;
    engine.inspectClock(intentMachine.intent);
    void requestUserPlayback(() => engine.play(), recoveryRef.current).catch((error: unknown) => {
      if (generation !== startGenerationRef.current || engineRef.current !== engine) return;
      const reason = error instanceof Error ? error.message : String(error);
      setRendererCondition(false);
      setStalled(reason);
      setAudioInterruption(
        'Meditation audio could not restart. Your actual sitting time is still shown.',
        engine.currentTime,
        false,
      );
      trace.anomaly('error', { phase: 'resume', message: reason });
      reportStall('resume-failed');
      syncMediaSession();
    });
  };

  const begin = async (prepared: Prepared) => {
    const generation = ++startGenerationRef.current;
    startingRef.current = true;
    setStarting(true);
    setProblem(null);
    setStalled(null);
    clearAudioInterruption();
    reportedStallsRef.current.clear();
    try {
      // The native shell must not create an AudioContext: its audio lives in another
      // process, and a second renderer here would take audio focus away from the one that
      // survives a locked screen. `ctx` is null on that path, and every context-dependent
      // thing below is guarded on it rather than duplicated, so both engines share one
      // event handler, one trace and one sit.
      const native = isNativeShell();
      // Container detection asks what this *browser* can decode. Native never decodes in
      // the WebView, so the probe would answer a question nobody is asking.
      const containers: Container[] = native ? ['opus'] : await detectContainers();
      if (!native && containers.length === 0) {
        throw new Error('this browser cannot decode any of our formats');
      }

      const ctx = native ? null : new AudioContext({ latencyHint: 'playback' });
      contextRef.current = ctx;
      intentMachine.dispatch('sit-started');

      // Open the trace before anything is scheduled, so a session that fails to start is
      // still described. The header carries every device fact that has ever turned out to
      // matter: the context's real sample rate (the tone profile was measured at 44 100
      // and may be being rendered at 48 000), the output latency, and which container the
      // probe actually chose.
      // Without a context the only monotonic clock left is performance.now(). It is not the
      // audio clock and the trace must not imply it is — but a trace with no clock at all
      // cannot order its own entries.
      const traceClock = ctx ? () => ctx.currentTime : () => performance.now() / 1000;
      trace.setClock(() => ({ domain: ctx ? 'web_audio' : 'performance',
        valueSec: traceClock(), uncertaintyMs: 0 }));
      const ota = native ? await Brahm.updateStatus().catch(() => null) : null;
      trace.open(`${Date.now().toString(36)}-${Math.floor(traceClock() * 1000)}`, {
        recordingId: prepared.recording.id,
        title: prepared.recording.title,
        targetSec: prepared.plan.targetSec,
        actualSec: prepared.plan.actualSec,
        cadence: prepared.plan.cadence,
        islands: prepared.plan.timeline.filter((x) => x.kind === 'speech').length,
        gaps: prepared.plan.timeline.filter((x) => x.kind !== 'speech').length,
        plan: planFingerprint(prepared.plan, prepared.recording),
        composition: prepared.plan.composition ?? null,
        releaseDigest: prepared.release.digest ?? null,
        manifestDigest: prepared.release.manifestDigest ?? null,
        build: BUILD_COMMIT,
        buildDirty: BUILD_DIRTY,
        manifestVersion: prepared.release.id,
        timingIntent: prepared.timing,
        tone: prepared.recording.tone,
        toneProfile: prepared.recording.toneProfile ?? null,
        // 48 000 is not a measurement on the native path, it is the protocol's rate; the
        // device's real output rate is only observable from inside the service.
        sampleRate: ctx?.sampleRate ?? FRAME_RATE,
        baseLatency: ctx?.baseLatency ?? null,
        outputLatency: ctx
          ? (ctx as AudioContext & { outputLatency?: number }).outputLatency ?? null
          : null,
        containers: containers.join(','),
        container: containers[0],
        engine: native ? 'native' : 'webaudio',
        ...(native ? { ota } : {}),
        // Which app this actually was. A trace from 2026-09-10 reported `webaudio` and all
        // three containers, which meant it had run the browser path — but nothing in it could
        // say whether that was the PWA or the native shell with a broken platform check, and
        // those need opposite fixes. `https://localhost` is the shell and nothing else is.
        origin: location.origin,
        userAgent: navigator.userAgent,
        pageInstanceId: PAGE_INSTANCE_ID,
        wasDiscarded: WAS_DISCARDED,
        keepAwakeEffective: s.keepAwake,
        wakeLockRequestResult: s.keepAwake ? 'pending' : 'disabled',
        wakeLockState: s.keepAwake ? 'pending' : 'disabled',
        contextInitialState: ctx?.state ?? 'native',
      });

      let engine: AudioEngine;
      if (ctx) {
        const loader = new SourceLoader(ctx, new ByteStore(), {
          // relative-url-ok: the web engine only, inside `if (ctx)`. The native path never
          // builds a SourceLoader; its audio is fetched by the Kotlin cache, not by JS.
          assetBase: prepared.release.assetBase,
          containers,
          storageBudgetBytes: s.storageBudgetBytes,
        });
        engine = new WebAudioEngine(loader, ctx);
      } else {
        // Lockstep, checked rather than assumed. The web build is bundled into the APK and
        // `server.url` is absent from capacitor.config.ts precisely so this can never be a
        // June page against a March bridge — but a shell that answers the wrong numbers is
        // a build mistake and must look like one. There is deliberately no fallback to Web
        // Audio: an implicit one would quietly reinstate the engine that a locked phone
        // already killed 328 s into a 647 s sit.
        const caps = await Brahm.protocolVersion();
        if (!bridgeIsCompatible(caps)) {
          throw new Error(
            `this native shell speaks protocol ${caps.protocolVersion} and bridge `
            + `${caps.bridgeVersion}; this build needs ${PROTOCOL_VERSION} and ${BRIDGE_VERSION}`,
          );
        }
        engine = new NativeAudioEngine(Brahm, {
          // The cache identity, and it has to change whenever a part is re-cut. `version` is
          // a schema number and has been 1 since the first manifest; `generatedAt` moves with
          // every regeneration. Over-invalidating costs a download, under-invalidating plays
          // the previous cut against the new island bounds.
          manifestVersion: prepared.release.id,
          budgetBytes: s.storageBudgetBytes,
        });
      }
      engineRef.current = engine;

      // Recovery and the statechange watch are about one specific failure: a Web Audio
      // renderer that the browser suspended or interrupted. The native service has no such
      // state — it is a foreground service holding audio focus in another process — so there
      // is nothing here to watch and nothing to resume. Running this block with a null
      // context would install a watchdog on a clock that does not exist.
      if (ctx) {
        recoveryRef.current = new RecoveryController({
          getIntent: () => intentMachine.intent,
          getState: () => ctx.state as RendererState,
          isVisible: () => document.visibilityState === 'visible',
          resumeContext: () => ctx.resume(),
          recoverRenderer: () => engine.recover(),
          onAttempt: onRecoveryAttempt,
        });

        let previousContextState = ctx.state as RendererState;
        const onContextState = () => {
          const next = ctx.state as RendererState;
          const detail = {
            previous: previousContextState,
            next,
            visibility: document.visibilityState,
            intent: intentMachine.intent,
          };
          if (contextStateIsAnomaly(previousContextState, next)) {
            trace.anomaly('ctx_state', detail);
          } else {
            trace.log('ctx_state', detail);
          }
          previousContextState = next;
          engine.inspectClock(intentMachine.intent);
          if (next === 'suspended' || next === 'interrupted') {
            setRendererCondition(false);
            intentMachine.dispatch('renderer-interrupted');
            setAudioInterruption(
              'Meditation audio paused while the phone was locked or the app was in the background. '
                + 'Your actual sitting time is still shown.',
            );
            reportStall(`context-${next}`);
            attemptRecovery('context-state');
          }
          syncMediaSession();
        };
        ctx.addEventListener('statechange', onContextState);
        contextCleanupRef.current = () => ctx.removeEventListener('statechange', onContextState);
      }

      engine.on((e: EngineEvent) => {
        if (e.type === 'timeupdate') {
          if (e.clock) trace.observeClock({ ...e.clock, valueSec: e.currentTime });
          setPosition(e.currentTime);
          const interrupted = interruptionRef.current;
          if (interrupted?.active && interrupted.clearsOnProgress
              && e.currentTime > interrupted.atSec + 0.05) {
            markAudioRecovered();
            setRendererCondition(true);
          }
          syncMediaSession();
        }
        else if (e.type === 'play') {
          setRendererCondition(true);
          // A new build must not reload the page out from under a sit; see state/update.ts.
          sitInProgress.value = true;
          trace.log('play', { intent: intentMachine.intent });
        }
        else if (e.type === 'pause') {
          setRendererCondition(false);
          trace.log('pause', { intent: intentMachine.intent });
        }
        else if (e.type === 'session_download') {
          const { type, ...detail } = e;
          trace.log(type, detail);
        }
        else if (e.type === 'download_hidden') {
          trace.anomaly('visibility', { event: 'session-download-incomplete',
            parts: e.parts, readyParts: e.readyParts, missingParts: e.missingParts,
            missingBytes: e.missingBytes });
        }
        else if (e.type === 'network_recovered') {
          trace.log('network_recovered', { asset: e.asset });
          setStalled(null);
          markAudioRecovered();
          setRendererCondition(engine.playing);
          syncMediaSession();
        }
        else if (e.type === 'stalled') {
          setRendererCondition(false);
          setStalled(e.reason);
          setAudioInterruption(
            'Meditation audio stopped. Your actual sitting time is still shown.',
            engine.currentTime,
            false,
          );
          trace.anomaly('stalled', { reason: e.reason, intent: intentMachine.intent });
          reportStall('assembly-stalled');
          syncMediaSession();
        }
        else if (e.type === 'ended') {
          setRendererCondition(false);
          intentMachine.dispatch('sit-ended');
          trace.log('ended');
          endSit(true);
        }
        else if (e.type === 'error') {
          setStalled(e.error.message);
          trace.anomaly('error', { message: e.error.message });
        }
        else if (e.type === 'tone_level') {
          trace.log('tone_level', { gap: e.gap, chunk: e.chunk,
            atSec: e.atSec, targetRms: e.targetRms });
        }
        else if (e.type === 'late_schedule') {
          trace.anomaly('late_schedule', { atSec: e.atSec, lateBySec: e.lateBySec });
        }
        else if (e.type === 'play_retry') {
          // An anomaly rather than a log line: the sit recovers, but the controller was
          // out of step with its own service and that is the thing being watched. Two
          // Begin taps were refused outright on 2026-09-14 before this existed.
          trace.anomaly('play_retry', { attempt: e.attempt });
        } else if (e.type === 'clock_drift') {
          setRendererCondition(false);
          const d = e.drift;
          trace.anomaly('clock_drift', {
            classification: d.classification,
            wallDeltaMs: d.wallDeltaMs,
            perfDeltaMs: d.perfDeltaMs,
            ctxDeltaSec: d.ctxDeltaSec,
            programSec: d.programSec,
            scheduledToSec: d.scheduledToSec,
            visibility: document.visibilityState,
            intent: intentMachine.intent,
          });
          // Deliberately does not name the cause. A thaw and a slow fetch both land here —
          // the watchdog sees the playhead pass the last covered moment and cannot tell
          // which — and this repo's rule is that the screen does not assert what it has
          // not measured. What IS known is the guarantee, so say that instead.
          const message = d.classification === 'scheduler-thaw'
            ? 'The meditation audio ran out. It continues from the last point known to have '
              + 'played, so no speech is skipped.'
            : 'Meditation audio stopped even though the device still reported it as running. '
              + 'Your actual sitting time is still shown.';
          setAudioInterruption(
            message,
            d.classification === 'scheduler-thaw' ? d.scheduledToSec : d.programSec,
          );
          reportStall(d.classification);
          const recovery = recoveryRef.current;
          if (recovery) void recovery.trigger('context-state');
        } else if (e.type === 'qa') {
          // The metric that caught the unlistenable first synthesis. Real tape sits at
          // 11.4x; anything we generate should be well under that.
          trace.log('qa', { amRatio: e.amRatio, atHz: e.atHz });
          if (e.amRatio > 11.4) {
            trace.anomaly('qa_fail', { amRatio: e.amRatio, atHz: e.atHz });
            report({ event: 'qa_fail', amRatio: e.amRatio, reason: `${e.atHz.toFixed(1)}Hz` });
          }
        }
      });

      const started = await prepareSessionStart(engine, prepared.recording, prepared.plan,
        prepared.timing, (recording, targetSec, options) => assertEligible({ recording,
          targetSec, cadence: prepared.plan.cadence,
          desiredSpeechRatio: s.desiredSpeechRatio ?? undefined, ...options }).plan, {
          isValid: () => startGenerationRef.current === generation,
          onPlanResolved: async (plan) => {
            const assembly = await resolveAssembly(prepared.recording, plan, { id: prepared.release.id,
              ...(prepared.release.digest ? { digest: prepared.release.digest } : {}) }, { planProvenance: 'captured' });
            if (startGenerationRef.current !== generation) throw new StaleSessionStartError();
            prepared = { ...prepared, plan };
            preparedRef.current = prepared;
            setPrepared(prepared);
            trace.amendHeader({ assembly, assemblyDigest: assembly.digest,
              plan: planFingerprint(plan, prepared.recording), composition: plan.composition ?? null, targetSec: plan.targetSec,
              actualSec: plan.actualSec, islands: plan.timeline.filter(x => x.kind === 'speech').length,
              gaps: plan.timeline.filter(x => x.kind === 'tone').length });
            const sessionId = trace.snapshot()?.id;
            if (sessionId) {
              archiveWriteRef.current = saveResolvedSession(sessionId, assembly).then(result => {
                if (trace.snapshot()?.id === sessionId) trace.amendHeader({ archiveStored: result.stored,
                  ...(result.reason ? { archiveProblem: result.reason } : {}) });
              }).catch(error => {
                if (trace.snapshot()?.id === sessionId) trace.amendHeader({ archiveStored: false,
                  archiveProblem: String(error) });
              });
            }
          },
        });
      // Deadline preparation may publish several provisional plans. Link only the final one.
      if (prepared.preview) {
        previewTrace.start(started.plan, prepared.recording, prepared.preview);
      }
      trace.amendHeader({ playbackRequestedAtMs: started.playbackRequestedAtMs,
        startupDelayMs: started.startupDelayMs, playbackRequestClock: 'wall',
        deadlineLaunchUncertainty: prepared.timing.kind === 'deadline' ? 'renderer-launch-unmeasured' : null });
      startWallElapsed();

      recordPlay({
        recordingId: prepared.recording.id,
        at: Date.now(),
        storiesHeard: prepared.recording.stories.map((x) => x.id),
        themes: prepared.recording.themes,
        completed: false,
      });
      const targetBucketMin = Math.round(targetSec(timingIntent.value) / 300) * 5;
      startSitMarker({
        startedAt: Date.now(),
        targetBucketMin,
        cadence: s.cadence,
      });
      report({
        event: 'session_start',
        targetBucketMin,
        cadence: s.cadence,
        container: containers[0],
        engine: native ? 'native' : 'webaudio',
      });

      setScreen('player');
      // The next visit to the home screen draws again, now that this recording is in the
      // history and the recency rule can push it down.
      choiceRef.current = null;
      // The native shell already has a media session — Media3's, owned by the foreground
      // service, which is what draws the notification and answers the lock screen. A second
      // session from the WebView would compete with it for the same transport controls.
      if (!native && 'mediaSession' in navigator) {
        const media = new MediaSessionController(navigator.mediaSession);
        mediaRef.current = media;
        media.start(new MediaMetadata({
          title: prepared.recording.title,
          artist: 'Ajahn Brahm',
          album: new Date(prepared.recording.recordedOn).getFullYear().toString(),
          artwork: [{ src: '/icon-512.png', sizes: '512x512', type: 'image/png' }],
        }), () => {
          intentMachine.dispatch('user-play');
          attemptRecovery('media-session-play');
        }, () => {
          intentMachine.dispatch('user-pause');
          engine.pause();
          clearAudioInterruption();
          syncMediaSession();
        });
        syncMediaSession();
      }

      const wakeRequest = 'wakeLock' in navigator
        ? () => navigator.wakeLock.request('screen')
        : undefined;
      const wake = new WakeLockController({
        request: wakeRequest,
        isVisible: () => document.visibilityState === 'visible',
        onResult: (result: WakeLockResult, error?: string) => {
          trace.amendHeader({
            wakeLockState: result,
            ...(result === 'released' ? {} : { wakeLockRequestResult: result }),
          });
          trace.log('wake_lock', { result, error: error ?? null });
        },
      });
      wakeRef.current = wake;
      void wake.start(s.keepAwake);
    } catch (e) {
      if (e instanceof StaleSessionStartError || generation !== startGenerationRef.current) return;
      trace.anomaly('error', { phase: 'begin', message: (e as Error).message });
      contextCleanupRef.current?.();
      contextCleanupRef.current = null;
      recoveryRef.current?.cancel();
      recoveryRef.current = null;
      wakeRef.current?.stop();
      wakeRef.current = null;
      mediaRef.current?.stop();
      mediaRef.current = null;
      engineRef.current?.destroy();
      engineRef.current = null;
      setRendererCondition(false);
      contextRef.current = null;
      intentMachine.dispatch('sit-ended');
      finishSitMarker();
      stopWallElapsed();
      const traceId = trace.snapshot()?.id;
      trace.close({ natural: false, startFailed: true });
      const failedTrace = trace.recent().find(session => session.id === traceId);
      if (failedTrace) void archiveWriteRef.current.then(() => finalizeArchivedSession(failedTrace));
      closingRef.current++;
      void uploadClosedSession(
        trace.recent().find((session) => session.id === traceId),
        trace.exportJson(),
      ).finally(() => { closingRef.current--; });
      setProblem(`Could not start: ${(e as Error).message}`);
    } finally {
      if (generation === startGenerationRef.current) {
        startingRef.current = false;
        setStarting(false);
      }
    }
  };

  const endSit = (natural: boolean) => {
    ++startGenerationRef.current;
    startingRef.current = false;
    setStarting(false);
    const prepared = preparedRef.current;
    setConfirmingExit(false);
    const engine = engineRef.current;
    const played = engine?.currentTime ?? position;
    intentMachine.dispatch('sit-ended');
    contextCleanupRef.current?.();
    contextCleanupRef.current = null;
    recoveryRef.current?.cancel();
    recoveryRef.current = null;
    mediaRef.current?.stop();
    mediaRef.current = null;
    wakeRef.current?.stop();
    wakeRef.current = null;
    engine?.destroy();
    engineRef.current = null;
    setRendererCondition(false);
    contextRef.current = null;
    sitInProgress.value = false;
    finishSitMarker();
    stopWallElapsed();
    if (prepared) {
      finishPlay(prepared.recording.id, played, prepared.plan.actualSec);
      report({
        event: 'session_end',
        completion: Math.round((played / prepared.plan.actualSec) * 20) / 20,
        cadence: prepared.plan.cadence,
        outcome: natural ? 'completed' : 'ended-early',
      });
    }
    const traceId = trace.snapshot()?.id;
    trace.close({ natural, playedSec: Math.round(played * 100) / 100 });
    const closedTrace = trace.recent().find(session => session.id === traceId);
    if (closedTrace) void archiveWriteRef.current.then(() => finalizeArchivedSession(closedTrace));
    closingRef.current++;
    const upload = uploadClosedSession(
      trace.recent().find((session) => session.id === traceId),
      trace.exportJson(),
    ).finally(() => { closingRef.current--; });
    if (natural) {
      if (diagnosticsTimerRef.current !== null) clearTimeout(diagnosticsTimerRef.current);
      setDiagnosticsStatus('sending');
      void upload.then((result) => {
        setDiagnosticsStatus(diagnosticsNotice(result));
        diagnosticsTimerRef.current = setTimeout(() => {
          setDiagnosticsStatus(null);
          diagnosticsTimerRef.current = null;
        }, 4500) as unknown as number;
      });
    }
    setScreen(natural ? 'done' : 'home');
    setPosition(0);
    setWallElapsed(0);
    setStalled(null);
    clearAudioInterruption();
  };

  // A back press during a sit asks before it ends anything; see state/back-guard.ts.
  const inSit = screen === 'player';
  useEffect(() => {
    if (!inSit) return;
    const onBack = () => setConfirmingExit((open) => !open);
    return isNativeShell() ? guardNativeBack(window, onBack) : guardBack(window, onBack);
  }, [inSit]);

  useEffect(() => () => {
    ++startGenerationRef.current;
    contextCleanupRef.current?.();
    recoveryRef.current?.cancel();
    mediaRef.current?.stop();
    wakeRef.current?.stop();
    engineRef.current?.destroy();
    stopWallElapsed();
    if (diagnosticsTimerRef.current !== null) clearTimeout(diagnosticsTimerRef.current);
  }, []);

  // A discard has no final event; the next load records it above. These listeners cover
  // the recoverable cases and flush each last signal before Chrome freezes freezable work.
  useEffect(() => {
    const onVisibility = () => {
      const state = document.visibilityState;
      trace.anomaly('visibility', {
        state,
        intent: intentMachine.intent,
        pageInstanceId: PAGE_INSTANCE_ID,
      });
      noteSitLifecycle(`visibility-${state}`);
      void wakeRef.current?.visibilityChanged();
      if (state === 'hidden') {
        trace.flush();
        previewTrace.flush();
        cellMemory.current.clear();
      } else {
        updateWallElapsed();
        attemptRecovery('visibility-visible');
      }
    };
    const onPageShow = (event: PageTransitionEvent) => {
      trace.log('page_lifecycle', {
        event: 'pageshow',
        persisted: event.persisted,
        intent: intentMachine.intent,
        pageInstanceId: PAGE_INSTANCE_ID,
      });
      updateWallElapsed();
      attemptRecovery('pageshow');
    };
    const onPageHide = (event: PageTransitionEvent) => {
      trace.log('page_lifecycle', {
        event: 'pagehide',
        persisted: event.persisted,
        intent: intentMachine.intent,
        pageInstanceId: PAGE_INSTANCE_ID,
      });
      noteSitLifecycle(event.persisted ? 'pagehide-bfcache' : 'pagehide');
      trace.flush();
      cellMemory.current.clear();
      if (canUploadPreviews()) {
        previewTrace.seal();
        sweepPreviewChunks(canUploadPreviews);
      }
    };
    const onFreeze = () => {
      trace.anomaly('page_lifecycle', {
        event: 'freeze',
        intent: intentMachine.intent,
        pageInstanceId: PAGE_INSTANCE_ID,
      });
      noteSitLifecycle('freeze');
      trace.flush();
    };
    const onResume = () => {
      trace.log('page_lifecycle', {
        event: 'resume',
        intent: intentMachine.intent,
        pageInstanceId: PAGE_INSTANCE_ID,
      });
      updateWallElapsed();
      attemptRecovery('page-resume');
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('freeze', onFreeze);
    document.addEventListener('resume', onResume);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('freeze', onFreeze);
      document.removeEventListener('resume', onResume);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  // ------------------------------------------------------------ render

  const banner = useMemo(
    () => problem ?? (screen === 'home' ? drawProblem : null) ?? manifestError.value,
    [problem, drawProblem, screen, manifestError.value],
  );

  // The watermark rides along on first run too. A fresh install is exactly when someone
  // wants to know which build they just put on the phone, and this screen is the first
  // thing it shows them.
  if (!s.onboarded) return <><Onboarding /><BuildMark /></>;
  if (loading.value && !manifest.value) {
    return <div class="screen"><div class="shell"><p class="dim">Loading the library…</p></div></div>;
  }

  return (
    <>
      {banner && (
        <div role="alert" class="shell" style={{
          padding: 'var(--gap-2) var(--gap-3)', background: 'var(--raised)',
          borderBottom: '1px solid var(--rule)', color: 'var(--warn)',
          fontSize: 'var(--step--1)',
        }}>
          {banner}
        </div>
      )}

      {screen === 'home' && (
        <Home
          key={manifest.value?.generatedAt}
          preview={homePreview}
          starting={starting}
          onSit={sit}
          onSomethingElse={somethingElse}
          onLibrary={() => setScreen('library')}
          onSettings={() => setScreen('settings')}
        />
      )}

      {screen === 'player' && prepared && (
        <Player
          recording={prepared.recording}
          plan={prepared.plan}
          position={position}
          wallElapsed={wallElapsed}
          playing={playbackIntent === 'playing' && rendererPlaying
            && interruption?.active !== true && stalled === null}
          interruption={interruption?.message ?? null}
          stalled={stalled}
          confirmingExit={confirmingExit}
          dimDuringSit={s.dimDuringSit}
          onKeepSitting={() => setConfirmingExit(false)}
          onPause={() => {
            intentMachine.dispatch('user-pause');
            engineRef.current?.pause();
            clearAudioInterruption();
            syncMediaSession();
          }}
          onResume={() => {
            resumeUserPlayback();
            syncMediaSession();
          }}
          onEnd={() => endSit(false)}
        />
      )}

      {screen === 'library' && (
        <Library
          onPick={(r) => { cellMemory.current.clear(); choiceRef.current = pickChoice(r); pendingTrigger.current = 'library-pick'; setScreen('home'); }}
          onBack={() => setScreen('home')}
        />
      )}

      {screen === 'settings' && (
        <Settings onBack={() => setScreen('home')}
                  onDiagnostics={() => setScreen('debug')}
                  onAccount={() => setScreen('account')} />
      )}
      {screen === 'account' && <Account onBack={() => setScreen('settings')} />}
      {screen === 'debug' && <Debug onBack={() => setScreen('settings')} />}

      {screen === 'done' && prepared && (
        <Done
          recording={prepared.recording}
          plan={prepared.plan}
          diagnosticsStatus={diagnosticsStatus}
          onDone={() => {
            if (diagnosticsTimerRef.current !== null) clearTimeout(diagnosticsTimerRef.current);
            diagnosticsTimerRef.current = null;
            setDiagnosticsStatus(null);
            setScreen('home');
          }}
        />
      )}

      {/* Every screen but the one someone is meditating in front of. */}
      {screen !== 'player' && <BuildMark />}
    </>
  );
}
